import { useCallback, useEffect, useRef, useState } from 'react';
import css from './index.module.scss';
import {
  IFilterState,
  IQuoteFormState,
  IQuotePrefill,
  IWeightSystemOption
} from '@helpers/types/quotes';
import EnquiryFilters from './components/filters';
import {
  CURRENCY,
  ENQUIRY_TYPE,
  MARKET_TYPE,
  PORT_FIELD_TYPES,
  WEIGHT_SYSTEM
} from '@helpers/constants';
import { DEFAULT_CURRENCY, getApiErrorMessage } from './components/helper';
import notify from '@helpers/toastify-helper';
import { fetchEntityList, getPaymentTermsFilterOptions } from '@services/order.service';
import {
  fetchQuoteCustomerList,
  fetchQuoteProductList,
  submitConsolidationQuote,
  submitNonConsolidationQuote,
  updateNonConsolidationEnquiry
} from '@services/quote.service';
import { fetchPortList } from '@services/util.service';
import { Drawer, IconNode, Switch } from '@components/base';
import Images from '@assets/images';
import EnquiryForm from './components/enquiry-form';
import { useBeforeUnloadAndNavigate } from '@helpers/hooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { enquiryFormValidation } from '@helpers/yup/add-quote.schema';
import DomesticQuote from './components/domestic';
import InternationalQuote from './components/international-quote';
import { hasNonZeroPriceComponent } from '@helpers/utils';

interface IQuoteFormWrapperProps {
  data?: any;
  prefill?: IQuotePrefill | null;
  showQuoteForm: boolean;
  onClose: (data: any) => void;
  onSubmitClose: () => void;
  isConsolidation?: boolean;
  isFcl?: boolean;
  isMtt?: boolean;
  isDuplicate?: boolean;
  isDomestic?: boolean;
  isDraft?: boolean;
}

const QuoteFormWrapper = (props: IQuoteFormWrapperProps) => {
  const {
    data,
    prefill,
    showQuoteForm,
    onClose,
    onSubmitClose,
    isConsolidation = false,
    isFcl = true,
    isMtt = false,
    isDuplicate = false,
    isDomestic = false
  } = props;
  const navigate = useNavigate();

  const [currentFormData, setCurrentFormData] = useState(null);

  // Links an enquiry created from a v2 customer enquiry back to its source
  const withSourceEnquiry = (reqBody: any) =>
    prefill && reqBody ? { ...reqBody, source_enquiry_id: prefill.customerEnquiryId } : reqBody;

  const fetchFormCurrentValue = (data: any) => {
    setCurrentFormData(data);
  };

  useBeforeUnloadAndNavigate();
  const [quoteFormState, setQuoteFormState] = useState<IQuoteFormState>({
    isLoading: false,
    activeStep: 1,
    paymentTermsOptions: [],
    entityOptions: [],
    productOptions: [],
    customerOptions: [],
    selectedCurrency: [DEFAULT_CURRENCY],
    selectedWeightSystemChildOptions: WEIGHT_SYSTEM.reduce(
      (acc: any, system: IWeightSystemOption) => {
        if (system.childOptions) {
          acc = acc.concat(system.childOptions);
        }
        return acc;
      },
      []
    ),
    portOfLoadingOptions: [],
    portOfDischargeOptions: [],
    firstFormReqBody: {},
    portOptions: [],
    enquiryId: null,
    duplicateEnquiry: isDuplicate,
    firstFormData: null,
    enquiryType: isDomestic ? ENQUIRY_TYPE.DOMESTIC : ENQUIRY_TYPE.INTERNATIONAL,
    isDraft: props.isDraft ?? false
  });

  const {
    isLoading,
    activeStep,
    paymentTermsOptions,
    entityOptions,
    customerOptions,
    firstFormReqBody,
    portOptions,
    enquiryId,
    enquiryType,
    duplicateEnquiry
  } = quoteFormState;

  const [filterState, setFilterState] = useState<IFilterState>({
    fcl: isFcl,
    mtt: isMtt,
    consolidation: isConsolidation
  });

  const { consolidation, fcl, mtt } = filterState;

  const enquiryForm = useForm<any>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(enquiryFormValidation),
    defaultValues: {
      enquiryNumber: null,
      customer: prefill?.enquiryForm.customer ?? null,
      entity: null,
      Createddate: null,
      payment_terms: [
        {
          payment_term: null,
          percentage: null,
          days: null
        }
      ],
      priceValidUntill: null,
      expectedDeliverySchedule: null,
      remarks: prefill?.enquiryForm.remarks ?? ''
    }
  });

  const handleBackClick = () => {
    setQuoteFormState((prevState) => ({
      ...prevState,
      activeStep: 1
    }));
  };

  const getPaymentTermsOptions = async () => {
    setQuoteFormState((prevState: IQuoteFormState) => ({ ...prevState, isLoading: true }));
    const response = await getPaymentTermsFilterOptions();
    if (response?.success) {
      setQuoteFormState((prevState: IQuoteFormState) => ({
        ...prevState,
        paymentTermsOptions: response.data?.payment_terms_options,
        isLoading: false
      }));
    } else {
      notify({
        severity: 'error',
        title: 'Error',
        message: response.message ?? 'Unable to fetch payment terms'
      });
      setQuoteFormState((prevState: IQuoteFormState) => ({ ...prevState, isLoading: false }));
    }
  };

  const getEntityList = async () => {
    setQuoteFormState((prevState: IQuoteFormState) => ({ ...prevState, isLoading: true }));
    const response = await fetchEntityList();
    if (response?.success) {
      setQuoteFormState((prevState: IQuoteFormState) => ({
        ...prevState,
        entityOptions: response?.data?.results ?? [],
        isLoading: false
      }));
    } else {
      setQuoteFormState((prevState: IQuoteFormState) => ({ ...prevState, isLoading: false }));
      notify({
        severity: 'error',
        message: response?.message
      });
    }
  };

  const filterProductOptions = async (event: any, param?: any) => {
    if (param) {
      const response = await fetchQuoteProductList({ product_name: param });
      if (response?.success) {
        setQuoteFormState((prevState: IQuoteFormState) => ({
          ...prevState,
          productOptions: response?.data?.results ?? []
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
      }
    } else {
      const response = await fetchQuoteProductList();
      if (response?.success) {
        setQuoteFormState((prevState: IQuoteFormState) => ({
          ...prevState,
          productOptions: response?.data?.results ?? []
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
      }
    }
  };

  const filterPortList = async () => {
    const data = await fetchPortList({ port_type: 'seaport' });
    if (data?.success) {
      setQuoteFormState((prevState: IQuoteFormState) => ({
        ...prevState,
        portOptions: data.data.filter_options,
        portOfLoadingOptions: data.data.filter_options,
        portOfDischargeOptions: data.data.filter_options
      }));
    }
  };

  const filterPorts = async (event?: any, param?: any, fieldType?: string) => {
    if (param) {
      const portList = await fetchPortList({ search_text: param, port_type: 'seaport' });
      if (portList?.success) {
        fieldType === PORT_FIELD_TYPES.PORT_OF_LOADING
          ? setQuoteFormState((prevState: IQuoteFormState) => ({
              ...prevState,
              portOfLoadingOptions: portList.data.filter_options
            }))
          : setQuoteFormState((prevState: IQuoteFormState) => ({
              ...prevState,
              portOfDischargeOptions: portList.data.filter_options
            }));
      } else {
        notify({
          message: portList.error ?? 'Internal Server Error',
          severity: 'error'
        });
      }
    } else {
      fieldType === PORT_FIELD_TYPES.PORT_OF_LOADING
        ? setQuoteFormState((prevState: IQuoteFormState) => ({
            ...prevState,
            portOfLoadingOptions: portOptions
          }))
        : setQuoteFormState((prevState: IQuoteFormState) => ({
            ...prevState,
            portOfDischargeOptions: portOptions
          }));
    }
  };

  const filterCustomerOptions = async (event: any, param?: any) => {
    if (param) {
      const response = await fetchQuoteCustomerList({ search: param });
      if (response?.success) {
        setQuoteFormState((prevState: IQuoteFormState) => ({
          ...prevState,
          customerOptions: response?.data ?? []
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
      }
    } else {
      const response = await fetchQuoteCustomerList();
      if (response?.success) {
        setQuoteFormState((prevState: IQuoteFormState) => ({
          ...prevState,
          customerOptions: response?.data ?? []
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
      }
    }
  };

  const onFormSubmit = async (data: any) => {
    let totalPercentage = 0,
      isError = false;
    data?.payment_terms?.forEach((item: any) => {
      totalPercentage += parseInt(item.percentage);
      if (item?.payment_term?.days && (!item?.days || parseInt(item?.days) <= 0)) {
        notify({
          message: `Enter valid number of days for ${item?.payment_term?.payment_term}`,
          severity: 'error'
        });
        isError = true;
        return;
      }
    });
    if (totalPercentage !== 100) {
      notify({
        message: 'Total percentage in payment terms should be 100',
        severity: 'error'
      });
      isError = true;
      return;
    }
    if (isError) return;
    const reqBody: any = withSourceEnquiry({ ...firstFormReqBody });
    reqBody.entity = data.entity.entity_id;
    reqBody.remarks = data.remarks;
    reqBody.last_valid_date = data.priceValidUntill
      ? `${moment(data.priceValidUntill).format('YYYY-MM-DD')}`
      : null;
    reqBody.expected_delivery_schedule = data.expectedDeliverySchedule;
    reqBody.payment_term = (data.payment_terms || []).map((term: any) => {
      const obj: any = {};
      obj['payment_term'] = term.payment_term?.payment_term;
      obj['percentage'] = parseInt(term.percentage);
      if (term.days) {
        obj['days'] = parseInt(term.days);
      }
      return obj;
    });
    reqBody.hubspot_customer_id = data.customer?.id;
    reqBody.customer_name = data.customer.customer_name;

    const response = consolidation
      ? await submitConsolidationQuote(reqBody)
      : enquiryId
      ? await updateNonConsolidationEnquiry(enquiryId, reqBody)
      : await submitNonConsolidationQuote(reqBody);

    if (response.success) {
      notify({
        severity: 'success',
        message: response.data.message
      });
      onSubmitClose();
      navigate('/quote/detail/' + (response?.data.enquiry || enquiryId || ''));
    } else {
      notify({
        severity: 'error',
        message: getApiErrorMessage(response)
      });
    }
  };

  const saveAsDraft = async (draftReqBody: any) => {
    const reqBody = withSourceEnquiry(draftReqBody);
    const draftValidation = hasNonZeroPriceComponent(reqBody);
    if (!draftValidation) {
      notify({
        severity: 'error',
        message: 'Atleast One Price is Required'
      });
      return;
    }
    const response = consolidation
      ? await submitConsolidationQuote(reqBody)
      : await submitNonConsolidationQuote(reqBody);

    if (response.success) {
      notify({
        severity: 'success',
        message: response.data.message
      });
      onSubmitClose();
      navigate('/quote?activeTab=cost_calculator', { replace: true });
    } else {
      notify({
        severity: 'error',
        message: getApiErrorMessage(response)
      });
    }
  };

  const editEnquirySubmit = async (enquiryId: string, reqBody: any) => {
    const response = await updateNonConsolidationEnquiry(enquiryId, reqBody);
    if (response.success) {
      notify({
        severity: 'success',
        message: response.data.message
      });
      onSubmitClose();
    } else {
      notify({
        severity: 'error',
        message: getApiErrorMessage(response)
      });
    }
  };

  useEffect(() => {
    getPaymentTermsOptions();
    getEntityList();
    filterCustomerOptions(null, '');
    filterPortList();
  }, []);

  useEffect(() => {
    if (data) {
      const selectedChildWeights = WEIGHT_SYSTEM.find(
        (item: IWeightSystemOption) => item.value === data.weight_system
      )?.childOptions;
      setQuoteFormState((prevState: IQuoteFormState) => ({
        ...prevState,
        enquiryType:
          data.market_type === MARKET_TYPE.DOMESTIC
            ? ENQUIRY_TYPE.DOMESTIC
            : ENQUIRY_TYPE.INTERNATIONAL,
        selectedWeightSystemChildOptions: selectedChildWeights || [],
        selectedCurrency:
          data.market_type === MARKET_TYPE.DOMESTIC
            ? [CURRENCY.find((item) => item.value === data.currency_type_one) || DEFAULT_CURRENCY]
            : [
                CURRENCY.find((item) => item.value === data.currency_type_one) || DEFAULT_CURRENCY,
                CURRENCY.find((item) => item.value === data.currency_type_two) || DEFAULT_CURRENCY
              ],
        enquiryId: duplicateEnquiry ? null : data.enquiry_id
      }));

      setFilterState((prevState: IFilterState) => ({
        ...prevState,
        fcl: data.container_type === 'FCL' || data.container_type === 'FTL',
        mtt: data.sourcing_type === 'MTT',
        consolidation: data?.is_consolidation
      }));
      enquiryForm.reset({
        enquiryNumber: {
          value: data.enquiry_id,
          label: data.readable_enquiry_number
        },
        customer: data?.hubspot_customer_id
          ? {
              id: data.hubspot_customer_id,
              customer_name: data.customer_name
            }
          : null,
        entity: data.entity
          ? {
              entity_id: data.entity?.entity_id,
              name: data.entity?.name
            }
          : null,
        Createddate: new Date(data.created_on),
        priceValidUntill: data?.last_valid_date && new Date(data.last_valid_date),
        remarks: data.remarks || '',
        payment_terms:
          data.payment_terms.length > 0
            ? data.payment_terms.map((term: any) => {
                return {
                  payment_term: {
                    payment_term: term.payment_term,
                    days: term.days ? true : false,
                    percentage: term.percentage ? true : false
                  },
                  percentage: term.percentage,
                  days: term.days
                };
              })
            : [
                {
                  payment_term: null,
                  percentage: null,
                  days: null
                }
              ],
        expectedDeliverySchedule: data.expected_delivery_schedule
      });
    }
  }, [data]);

  const handleClose = () => {
    onClose(withSourceEnquiry(currentFormData));
  };

  return (
    <Drawer open={showQuoteForm} anchor="right" onClose={handleClose}>
      <div className={css.mainWrapper}>
        <div className={css.header}>
          <div className={css.quoteHeading}>
            {activeStep == 1 ? 'Quote Generator' : 'Convert To Enquiry'}
          </div>
          <div className={css.headerActions}>
            <div
              className={`${css.headerActionLabel} ${
                enquiryType === ENQUIRY_TYPE.INTERNATIONAL ? css.active : ''
              }`}
            >
              International
            </div>
            <Switch
              checked={enquiryType !== ENQUIRY_TYPE.INTERNATIONAL}
              onChange={(e: any) => {
                setQuoteFormState((prevState: IQuoteFormState) => ({
                  ...prevState,
                  enquiryType: e.target.checked ? ENQUIRY_TYPE.DOMESTIC : ENQUIRY_TYPE.INTERNATIONAL
                }));
              }}
            />
            <div
              className={`${css.headerActionLabel} ${
                enquiryType === ENQUIRY_TYPE.DOMESTIC ? css.active : ''
              }`}
            >
              Domestic
            </div>
            <IconNode
              src={Images.close}
              alt="quote icon"
              onClick={() => handleClose()}
              className={css.closeIcon}
            />
          </div>
        </div>
        {activeStep === 1 && (
          <div>
            {/* <EnquiryType quoteFormState={quoteFormState} setQuoteFormState={setQuoteFormState} /> */}

            <EnquiryFilters
              filterState={filterState}
              setFilterState={setFilterState}
              enquiryType={enquiryType}
            />

            {enquiryType === ENQUIRY_TYPE.INTERNATIONAL ? (
              <InternationalQuote
                quoteFormState={quoteFormState}
                setQuoteFormState={setQuoteFormState}
                filterState={filterState}
                filterProductOptions={filterProductOptions}
                filterPorts={filterPorts}
                data={data}
                prefill={prefill}
                onSubmitClose={onSubmitClose}
                saveAsDraft={saveAsDraft}
                editEnquirySubmit={editEnquirySubmit}
                fetchFormCurrentValue={fetchFormCurrentValue}
              />
            ) : (
              <DomesticQuote
                quoteFormState={quoteFormState}
                filterState={filterState}
                setQuoteFormState={setQuoteFormState}
                filterProductOptions={filterProductOptions}
                data={data}
                onSubmitClose={onSubmitClose}
                saveAsDraft={saveAsDraft}
                editEnquirySubmit={editEnquirySubmit}
                fetchFormCurrentValue={fetchFormCurrentValue}
              />
            )}
          </div>
        )}

        {activeStep === 2 && (
          <FormProvider {...enquiryForm}>
            <EnquiryForm
              paymentTermsOptions={paymentTermsOptions}
              handleBackClick={handleBackClick}
              entityOptions={entityOptions}
              onFormSubmit={onFormSubmit}
              customerOptions={customerOptions}
              filterCustomerOptions={filterCustomerOptions}
            />
          </FormProvider>
        )}
      </div>
    </Drawer>
  );
};

export default QuoteFormWrapper;
